







































































































































import { Vue, Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";
import HasValidation from "@/mixins/HasValidation";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Typings
import { ITicketForm, ITicketDepartment, ITicket } from "@/models/tickets";
import { IClient } from "@/models/clients";
import { IAccount } from "@/models/accounts";
import { IContractProduct } from "@/models/contracts";
import { Route } from "vue-router";
import _ from "@/boot/lodash";
import { ClientRoutes } from "@/data/enums/router";

@Component({
  components: {
    "files-provider": () =>
      import("@/components/app/global/files/filesProvider.vue"),
    "file-upload-button": () =>
      import("@/components/app/global/files/fileUploadButton.vue"),
    "file-list": () => import("@/components/app/global/files/fileList.vue"),
    "select-client-contract-product": () =>
      import("@/components/app/global/fields/selectClientContractProduct.vue")
  }
})
export default class TicketForm extends Mixins(
  RequestPromises,
  Context,
  HasValidation
) {
  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    filesProvider: Vue;
  };

  // Props

  @Prop({ default: false })
  public isModal!: boolean;

  @Prop({ default: undefined })
  public clientId!: IClient["id"];

  @Prop({ default: undefined })
  public accountId!: IAccount["id"];

  @Prop({ default: undefined })
  public leadId!: IClient["id"];

  @Prop({ required: true })
  public brandId!: IClient["brand_id"];

  @Prop({ default: () => ({}) })
  public contractProduct!: IContractProduct;

  @Prop({
    default: (): TicketForm["onSuccess"] => (vm, ticket) => {
      return vm.$router.push({
        name: vm.isAdmin
          ? `adminSupportTicket`
          : vm.isMockClientContext
          ? `admin__upmindTicket`
          : ClientRoutes.SUPPORT_TICKET,
        params: {
          ticketId: ticket.id
        }
      });
    }
  })
  public onSuccess!: (vm: TicketForm, ticket: ITicket) => Promise<Route>;

  // Data

  public formIsNotValid = false;
  public hasRequestError = false;
  public initialForm = {} as ITicketForm;
  public isLoading = true;
  public isProcessing = false;
  public isReloading = false;
  public showProductSelect = false;
  public ticketDepartments: ITicketDepartment[] = [];
  public form: ITicketForm = {
    account_id: "",
    body: "",
    client_id: "",
    lead_id: "",
    contract_product_id: null,
    is_private: false,
    subject: "",
    ticket_department_id: null
  };

  // Computed

  public get sanitizedForm() {
    return _.omitBy(this.form, value => _.isNil(value));
  }

  public get filteredDepartments() {
    if (!this.isAdmin)
      return _.filter(this.ticketDepartments, ["is_public", true]);
    return this.ticketDepartments;
  }

  public get isFormChanged(): boolean {
    return _.isEqual(this.form, this.initialForm);
  }

  // Life-cycle methods

  public created() {
    this.loadData();
  }

  // Methods

  public async loadData() {
    try {
      const [, ticketDepartments] = await Promise.all([
        this.getClientProductsCount(),
        this.$store.dispatch("constants/getDepartments", {
          fromCache: !this.isMockClientContext
        })
      ]);
      this.ticketDepartments = ticketDepartments;
      this.initialiseForm();
      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public initialiseForm() {
    this.form.client_id = this.clientId;
    this.form.account_id = this.accountId;
    this.form.lead_id = this.leadId;
    this.form.ticket_department_id =
      _.find(this.ticketDepartments, ["default", true])?.id || null;
    this.form.contract_product_id = this.contractProduct?.id || null;
    this.initialForm = _.clone(this.form);
  }

  public async getClientProductsCount() {
    if (this.contractProduct.id) return (this.showProductSelect = true);
    if (!this.accountId && !this.clientId) return;

    const { total } = await this.$store.dispatch(
      `data/${DataModules.CONTRACTS_PRODUCTS}/list`,
      {
        vm: this,
        returnData: false,
        storeData: false,
        ignoreStored: true,
        params: {
          limit: "count",
          ...(this.accountId ? { "filter[account_id]": this.accountId } : {})
        }
      }
    );
    this.showProductSelect = !!total;
  }

  public async validateBeforeSubmit() {
    const filesProvider = this.$refs.filesProvider as any;
    if (!filesProvider.hasValidAttachments) return;
    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");
      this.isProcessing = true;
      await filesProvider.uploadFiles();

      const newAttachments = _.map(
        _.get(this.$refs.filesProvider, "attachments", []),
        attachment => {
          return _.pick(attachment, ["id", "hash"]);
        }
      );

      const ticket = await this.createTicket(
        newAttachments.length ? { files: newAttachments } : {}
      );

      this.$buefy.toast.open({
        message: this.$t("_.ticket_created") as string
      });

      this.onSuccess(this, ticket);

      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public createTicket(attachments): Promise<ITicket> {
    return this.$store.dispatch(`data/${DataModules.TICKETS}/create`, {
      data: { ...this.sanitizedForm, ...attachments }
    });
  }

  // Watchers

  @Watch("isProcessing")
  public onIsProcessingChange() {
    this.$emit("isProcessing", this.isProcessing);
  }

  @Watch("formIsNotValid")
  public onFormIsNotValidChange(value) {
    this.$emit("isNotValid", value);
  }
}
